import React, { useCallback } from 'react';
import QuestionForm from './QuestionForm';
import { Question } from '../utils/questionService';

interface UnansweredQuestionsProps {
  unansweredQuestions: Question[];
  onUpdate: (newResponse: { id: string; answer: unknown; text: string }) => void;
  isSaving: boolean;
  completionMessage: string | null;
  error: Error | null;
}

const UnansweredQuestions: React.FC<UnansweredQuestionsProps> = ({ 
    unansweredQuestions, 
    onUpdate, 
    isSaving,
    completionMessage,
    error,
}) => {
  const currentQuestion = unansweredQuestions?.[0];

  const handleAnswer = useCallback((questionId: string, answer: unknown) => {
    if (!currentQuestion) return;
    const newResponse = { id: questionId, answer, text: currentQuestion.text };
    onUpdate(newResponse);
  }, [onUpdate, currentQuestion]);

  // Handle case when there are no unanswered questions
  if (!unansweredQuestions || unansweredQuestions.length === 0) {
    return null;
  }

  if (completionMessage) {
    return (
      <div className="bg-green-50 border-l-4 border-green-500 text-green-800 p-6 mb-8 rounded-2xl flex items-center gap-4 animate-fade-in">
        <span className="text-3xl" role="img" aria-label="done">✅</span>
        <div>
          <p className="font-bold text-lg mb-1">{completionMessage}</p>
        </div>
      </div>
    );
  }

  if (!currentQuestion) {
    return null;
  }

  return (
    <div data-testid="question-container" className="bg-yellow-50 border-l-4 border-yellow-500 text-yellow-800 p-6 mb-8 rounded-2xl animate-fade-in">
      <div className="flex items-center gap-3 mb-2">
        <span className="text-2xl" role="img" aria-label="question">❓</span>
        <h3 className="font-bold text-lg">Дополнительные вопросы</h3>
      </div>
      <p className="mb-2">Для уточнения результатов, пожалуйста, ответьте на несколько вопросов.</p>
      <div className="mt-4">
        {isSaving ? (
          <div className="text-yellow-700">Сохранение...</div>
        ) : error ? (
          <div className="text-red-600">Ошибка: {error.message}</div>
        ) : (
          <QuestionForm
            question={currentQuestion}
            onAnswer={handleAnswer}
            onPrevious={() => {}}
            response={null}
          />
        )}
      </div>
    </div>
  );
};

export default UnansweredQuestions;
